import { useUnitsStore } from "@/stores/unitsStore"

export const UNIT_OFFLINE_GRACE_MS = 30_000

export type QueuedCommandFailureReason = "timeout" | "offline"

export class QueuedCommandFailure extends Error {
  readonly reason: QueuedCommandFailureReason

  constructor(reason: QueuedCommandFailureReason) {
    super(reason)
    this.name = "QueuedCommandFailure"
    this.reason = reason
  }
}

type PendingWait = {
  unitIds: string[]
  resolve: () => void
  reject: (error: QueuedCommandFailure) => void
  timeoutId: ReturnType<typeof setTimeout>
}

const pendingWaits: PendingWait[] = []

export const isUnitReconnectOnline = (unitId: string): boolean => {
  const unit = useUnitsStore.getState().units[unitId]
  if (!unit) {
    return false
  }

  return unit._connectivity !== "degraded"
}

/** Units currently in the reconnect grace window (live store + degraded). */
export const getDegradedUnitIds = (unitIds: string[]): string[] => {
  const units = useUnitsStore.getState().units
  return [...new Set(unitIds.filter(Boolean))].filter(
    (unitId) => units[unitId]?._connectivity === "degraded",
  )
}

const areAllUnitsOnline = (unitIds: string[]): boolean =>
  unitIds.every(isUnitReconnectOnline)

const removeWait = (wait: PendingWait) => {
  const index = pendingWaits.indexOf(wait)
  if (index >= 0) {
    pendingWaits.splice(index, 1)
  }
}

const settleWaitsForOnlineUnits = () => {
  for (const wait of [...pendingWaits]) {
    if (!areAllUnitsOnline(wait.unitIds)) {
      continue
    }

    clearTimeout(wait.timeoutId)
    removeWait(wait)
    wait.resolve()
  }
}

const rejectWaitsForRemovedUnit = (unitId: string) => {
  for (const wait of [...pendingWaits]) {
    if (!wait.unitIds.includes(unitId)) {
      continue
    }

    clearTimeout(wait.timeoutId)
    removeWait(wait)
    wait.reject(new QueuedCommandFailure("offline"))
  }
}

/**
 * Resolves when units leave the degraded reconnect window.
 *
 * Only units that are currently `_connectivity === "degraded"` are waited on.
 * Already-online units and units missing from the live SSE store do not block —
 * otherwise multi-unit station commands hang until timeout even when the
 * command already ran (sibling offline/never-seen serials, or reconnect
 * finished before the toast registered).
 */
export const waitForUnitsReconnect = (
  unitIds: string[],
  graceMs: number = UNIT_OFFLINE_GRACE_MS,
): Promise<void> => {
  const degradedUnitIds = getDegradedUnitIds(unitIds)
  if (degradedUnitIds.length === 0) {
    return Promise.resolve()
  }

  if (areAllUnitsOnline(degradedUnitIds)) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const wait: PendingWait = {
      unitIds: degradedUnitIds,
      resolve,
      reject,
      timeoutId: setTimeout(() => {
        removeWait(wait)
        reject(new QueuedCommandFailure("timeout"))
      }, graceMs),
    }

    pendingWaits.push(wait)
  })
}

export const notifyUnitConnectivityOnline = (unitId: string) => {
  if (!isUnitReconnectOnline(unitId)) {
    return
  }

  settleWaitsForOnlineUnits()
}

export const notifyUnitConnectivityRemoved = (unitId: string) => {
  rejectWaitsForRemovedUnit(unitId)
}

export const resetQueuedCommandAwaiterForTests = () => {
  for (const wait of pendingWaits) {
    clearTimeout(wait.timeoutId)
  }
  pendingWaits.length = 0
}
